import React, { useCallback, useEffect, useMemo, useState } from "react";
import { notify } from "../utils/notify";
import { usePanelTheme } from "../context/themeContext";
import UIButton from "../components/UIButton";
import UILabel from "../components/UILabel";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  getCache,
  setCache,
  clearCacheByPrefix,
} from "../utils/cache";

const CLIENTS_CACHE_KEY = "bauheld:clients:list:v4";
const CLIENTS_CACHE_TTL = 5 * 60 * 1000;
const PROFILE_META_CACHE_TTL = 5 * 60 * 1000;
const PROFILE_PAGE_CACHE_TTL = 5 * 60 * 1000;

const PAGE_SIZE = 8;
const FALLBACK_LIMIT = 100;

const TYPE_OPTIONS = ["all", "private", "company", "subcontractor"];
const STATUS_OPTIONS = ["all", "active", "finished", "pending"];

const emptyClientForm = {
  type: "private",
  name: "",
  companyName: "",
  contactPerson: "",
  phone: "",
  email: "",
  billingAddress: "",
  worksiteAddress: "",
  city: "",
  vatNumber: "",
  serviceType: "",
  status: "active",
  notes: "",
};

const ui = {
  sq: {
    title: "Klientët",
    subtitle: "Menaxho klientët pa full reads të panevojshme.",
    search: "Kërko klient",
    type: "Tipi",
    status: "Statusi",
    all: "Të gjitha",
    private: "Privat",
    company: "Kompani",
    subcontractor: "Nënkontraktor",
    active: "Aktiv",
    finished: "Përfunduar",
    pending: "Në pritje",
    addClient: "Klient i ri",
    editClient: "Edito",
    deleteClient: "Fshije",
    refresh: "Refresh",
    profile: "Profili",
    invoices: "Faturat",
    income: "Hyrjet",
    payments: "Pagesat",
    overview: "Përmbledhje",
    totalInvoiced: "Total faturuar",
    totalIncome: "Total hyrje",
    totalPayments: "Total pagesa",
    openBalance: "Saldo e hapur",
    noClientSelected: "Zgjedh një klient për profilin.",
    page: "Faqe",
    prev: "Mbrapa",
    next: "Para",
    loading: "Duke ngarkuar...",
    noData: "Nuk ka të dhëna",
    save: "Ruaj",
    update: "Përditëso",
    cancel: "Anulo",
    name: "Emri",
    companyName: "Emri i kompanisë",
    contactPerson: "Kontakt person",
    phone: "Telefoni",
    email: "Email",
    billingAddress: "Adresa e faturimit",
    worksiteAddress: "Adresa e objektit",
    city: "Qyteti",
    vatNumber: "TVSH / UID",
    serviceType: "Lloji i shërbimit",
    notes: "Shënime",
    actions: "Veprime",
    createdAt: "Krijuar",
    dueDate: "Afati",
    amount: "Shuma",
    invoiceNo: "Nr. faturës",
    paid: "Paguar",
    date: "Data",
    confirmDelete: "A je i sigurt që dëshiron ta fshish këtë klient?",
    deleted: "Klienti u fshi.",
    saved: "Klienti u ruajt me sukses.",
    updated: "Klienti u përditësua me sukses.",
    quickInvoice: "Faturë e re",
    quickIncome: "Hyrje",
    quickPayment: "Pagesë",
  },
  de: {
    title: "Kunden",
    subtitle: "Kundenverwaltung ohne unnötige Full Reads.",
    search: "Kunden suchen",
    type: "Typ",
    status: "Status",
    all: "Alle",
    private: "Privat",
    company: "Firma",
    subcontractor: "Subunternehmer",
    active: "Aktiv",
    finished: "Abgeschlossen",
    pending: "Offen",
    addClient: "Neuer Kunde",
    editClient: "Bearbeiten",
    deleteClient: "Löschen",
    refresh: "Aktualisieren",
    profile: "Profil",
    invoices: "Rechnungen",
    income: "Einnahmen",
    payments: "Zahlungen",
    overview: "Übersicht",
    totalInvoiced: "Gesamt fakturiert",
    totalIncome: "Gesamteinnahmen",
    totalPayments: "Gesamtzahlungen",
    openBalance: "Offener Saldo",
    noClientSelected: "Wähle einen Kunden für das Profil.",
    page: "Seite",
    prev: "Zurück",
    next: "Weiter",
    loading: "Wird geladen...",
    noData: "Keine Daten",
    save: "Speichern",
    update: "Aktualisieren",
    cancel: "Abbrechen",
    name: "Name",
    companyName: "Firmenname",
    contactPerson: "Kontaktperson",
    phone: "Telefon",
    email: "E-Mail",
    billingAddress: "Rechnungsadresse",
    worksiteAddress: "Baustellenadresse",
    city: "Stadt",
    vatNumber: "MwSt / UID",
    serviceType: "Leistungstyp",
    notes: "Notizen",
    actions: "Aktionen",
    createdAt: "Erstellt",
    dueDate: "Fällig",
    amount: "Betrag",
    invoiceNo: "Rechnungsnr.",
    paid: "Bezahlt",
    date: "Datum",
    confirmDelete: "Diesen Kunden wirklich löschen?",
    deleted: "Kunde wurde gelöscht.",
    saved: "Kunde wurde gespeichert.",
    updated: "Kunde wurde aktualisiert.",
    quickInvoice: "Neue Rechnung",
    quickIncome: "Einnahme",
    quickPayment: "Zahlung",
  },
};

function formatMoney(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";
  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleDateString("de-DE");
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-DE");
}

function safeLower(v) {
  return String(v || "").toLowerCase().trim();
}

function normalizeClient(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    type: data.type || "private",
    name: data.name || "",
    companyName: data.companyName || "",
    contactPerson: data.contactPerson || "",
    phone: data.phone || "",
    email: data.email || "",
    billingAddress: data.billingAddress || "",
    worksiteAddress: data.worksiteAddress || "",
    city: data.city || "",
    vatNumber: data.vatNumber || "",
    serviceType: data.serviceType || "",
    status: data.status || "active",
    notes: data.notes || "",
    createdAt: data.createdAt || "",
    updatedAt: data.updatedAt || "",
  };
}

async function getAllClients(force = false) {
  if (!force) {
    const cached = getCache(CLIENTS_CACHE_KEY, CLIENTS_CACHE_TTL);
    if (cached) return cached;
  }
  const snap = await getDocs(
    query(collection(db, "invoice_clients"), orderBy("createdAt", "desc"))
  );
  const rows = snap.docs.map(normalizeClient);
  setCache(CLIENTS_CACHE_KEY, rows);
  return rows;
}

function getProfileMetaKey(clientId) {
  return `bauheld:client-profile:meta:${clientId}:v4`;
}

function getProfilePageKey(clientId, tab, page) {
  return `bauheld:client-profile:${clientId}:${tab}:page:${page}:v4`;
}

function getClientNames(client) {
  return [client?.name, client?.companyName, client?.contactPerson]
    .filter(Boolean)
    .map(safeLower);
}

function recordMatchesClient(row, client) {
  if (!row || !client) return false;
  if (row.clientId && row.clientId === client.id) return true;
  if (row.client?.id && row.client?.id === client.id) return true;

  const names = getClientNames(client);
  if (!names.length) return false;

  const hay = [
    row.client,
    row.clientName,
    row.customer,
    row.customerName,
    row.vendor,
    row.name,
    row.companyName,
    row.client?.name,
    row.client?.companyName,
  ]
    .filter(Boolean)
    .map(safeLower)
    .join(" | ");

  return names.some((n) => n && hay.includes(n));
}

async function getFallbackRows(collectionName, client) {
  const snap = await getDocs(
    query(collection(db, collectionName), orderBy("createdAt", "desc"), limit(FALLBACK_LIMIT))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((row) => recordMatchesClient(row, client));
}

async function getProfileMeta(client, force = false) {
  const key = getProfileMetaKey(client.id);
  if (!force) {
    const cached = getCache(key, PROFILE_META_CACHE_TTL);
    if (cached) return cached;
  }

  let [invoiceRows, incomeRows, paymentRows] = await Promise.all([
    getDocs(query(collection(db, "invoices"), where("clientId", "==", client.id))),
    getDocs(query(collection(db, "income"), where("clientId", "==", client.id))),
    getDocs(query(collection(db, "payments"), where("clientId", "==", client.id))),
  ]);

  let invoiceDocs = invoiceRows.docs.map((d) => ({ id: d.id, ...d.data() }));
  let incomeDocs = incomeRows.docs.map((d) => ({ id: d.id, ...d.data() }));
  let paymentDocs = paymentRows.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (!invoiceDocs.length) invoiceDocs = await getFallbackRows("invoices", client);
  if (!incomeDocs.length) incomeDocs = await getFallbackRows("income", client);
  if (!paymentDocs.length) paymentDocs = await getFallbackRows("payments", client);

  const meta = {
    totalInvoiced: invoiceDocs.reduce((s, d) => s + Number(d.grandTotal || d.total || 0), 0),
    totalIncome: incomeDocs.reduce((s, d) => s + Number(d.amount || 0), 0),
    totalPayments: paymentDocs.reduce((s, d) => s + Number(d.amount || 0), 0),
    invoicesCount: invoiceDocs.length,
    incomeCount: incomeDocs.length,
    paymentsCount: paymentDocs.length,
  };
  meta.openBalance = Math.max(0, meta.totalInvoiced - meta.totalPayments);
  setCache(key, meta);
  return meta;
}

async function getPagedCollection({
  client,
  collectionName,
  page = 1,
  orderField = "createdAt",
  force = false,
}) {
  const cacheKey = getProfilePageKey(client.id, collectionName, page);
  if (!force) {
    const cached = getCache(cacheKey, PROFILE_PAGE_CACHE_TTL);
    if (cached) return cached;
  }

  let rows = [];
  let hasMore = false;

  const cursorMap =
    page > 1
      ? getCache(getProfilePageKey(client.id, collectionName, `${page}-cursor`), PROFILE_PAGE_CACHE_TTL)
      : null;

  let baseQuery = query(
    collection(db, collectionName),
    where("clientId", "==", client.id),
    orderBy(orderField, "desc"),
    limit(PAGE_SIZE)
  );

  if (page > 1) {
    const previousPageKey = getProfilePageKey(client.id, collectionName, page - 1);
    const previousPageData = getCache(previousPageKey, PROFILE_PAGE_CACHE_TTL);
    const lastVisible = previousPageData?.lastVisible || cursorMap?.lastVisible;
    if (lastVisible) {
      baseQuery = query(
        collection(db, collectionName),
        where("clientId", "==", client.id),
        orderBy(orderField, "desc"),
        startAfter(lastVisible),
        limit(PAGE_SIZE)
      );
    }
  }

  const snap = await getDocs(baseQuery);
  rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  hasMore = snap.docs.length === PAGE_SIZE;

  if (!rows.length) {
    const fallbackRows = await getFallbackRows(collectionName, client);
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    rows = fallbackRows.slice(start, end);
    hasMore = fallbackRows.length > end;
  }

  const payload = {
    rows,
    lastVisible: snap.docs[snap.docs.length - 1] || null,
    hasMore,
  };

  setCache(cacheKey, payload);
  if (payload.lastVisible) {
    setCache(getProfilePageKey(client.id, collectionName, `${page + 1}-cursor`), {
      lastVisible: payload.lastVisible,
    });
  }
  return payload;
}

export default function Clients({ language = "de", setActivePage }) {
  const { theme } = usePanelTheme();
  const t = ui[language] || ui.de;

  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingClients, setLoadingClients] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyClientForm);
  const [savingClient, setSavingClient] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [profileTab, setProfileTab] = useState("overview");
  const [profileMeta, setProfileMeta] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [tabPages, setTabPages] = useState({
    invoices: 1,
    income: 1,
    payments: 1,
  });
  const [tabRows, setTabRows] = useState({
    invoices: [],
    income: [],
    payments: [],
  });
  const [tabHasMore, setTabHasMore] = useState({
    invoices: false,
    income: false,
    payments: false,
  });

  const loadClients = useCallback(
    async (force = false) => {
      setLoadingClients(true);
      try {
        const rows = await getAllClients(force);
        setClients(rows);
        if (rows.length && !selectedClient) {
          setSelectedClient(rows[0]);
        } else if (selectedClient) {
          const freshSelected = rows.find((x) => x.id === selectedClient.id);
          if (freshSelected) setSelectedClient(freshSelected);
        }
      } catch (error) {
        console.error("Error loading clients:", error);
      } finally {
        setLoadingClients(false);
      }
    },
    [selectedClient]
  );

  const loadProfileMetaData = useCallback(async (client, force = false) => {
    if (!client?.id) return;
    setProfileLoading(true);
    try {
      const meta = await getProfileMeta(client, force);
      setProfileMeta(meta);
    } catch (error) {
      console.error("Error loading client profile meta:", error);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const loadTabData = useCallback(async (client, tab, page = 1, force = false) => {
    if (!client?.id || tab === "overview") return;
    setProfileLoading(true);
    try {
      const map = {
        invoices: { collectionName: "invoices", orderField: "createdAt" },
        income: { collectionName: "income", orderField: "createdAt" },
        payments: { collectionName: "payments", orderField: "createdAt" },
      };
      const cfg = map[tab];
      const payload = await getPagedCollection({
        client,
        collectionName: cfg.collectionName,
        orderField: cfg.orderField,
        page,
        force,
      });
      setTabRows((prev) => ({ ...prev, [tab]: payload.rows }));
      setTabHasMore((prev) => ({ ...prev, [tab]: payload.hasMore }));
      setTabPages((prev) => ({ ...prev, [tab]: page }));
    } catch (error) {
      console.error(`Error loading ${tab}:`, error);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients(false);
  }, [loadClients]);

  useEffect(() => {
    if (selectedClient?.id) {
      loadProfileMetaData(selectedClient, false);
      if (profileTab !== "overview") {
        loadTabData(selectedClient, profileTab, tabPages[profileTab] || 1, false);
      }
    }
  }, [selectedClient, profileTab, loadProfileMetaData, loadTabData]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const q = safeLower(search);
      const hay = [
        client.name,
        client.companyName,
        client.contactPerson,
        client.phone,
        client.email,
        client.city,
      ]
        .map(safeLower)
        .join(" ");
      const matchesSearch = !q || hay.includes(q);
      const matchesType = typeFilter === "all" || client.type === typeFilter;
      const matchesStatus = statusFilter === "all" || client.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [clients, search, typeFilter, statusFilter]);

  const resetForm = () => {
    setEditingClient(null);
    setFormData(emptyClientForm);
    setShowForm(false);
  };

  const startEdit = (client) => {
    setEditingClient(client);
    setFormData({
      type: client.type || "private",
      name: client.name || "",
      companyName: client.companyName || "",
      contactPerson: client.contactPerson || "",
      phone: client.phone || "",
      email: client.email || "",
      billingAddress: client.billingAddress || "",
      worksiteAddress: client.worksiteAddress || "",
      city: client.city || "",
      vatNumber: client.vatNumber || "",
      serviceType: client.serviceType || "",
      status: client.status || "active",
      notes: client.notes || "",
    });
    setShowForm(true);
  };

  const handleSaveClient = async () => {
    const companyName = String(formData.companyName || "").trim();
    const name = String(formData.name || "").trim();
    if (!companyName && !name) {
      notify.error(language === "de" ? "Bitte Name eingeben." : "Ju lutem shkruaj emrin.");
      return;
    }

    const payload = {
      type: formData.type || "private",
      name,
      companyName,
      contactPerson: String(formData.contactPerson || "").trim(),
      phone: String(formData.phone || "").trim(),
      email: String(formData.email || "").trim(),
      billingAddress: String(formData.billingAddress || "").trim(),
      worksiteAddress: String(formData.worksiteAddress || "").trim(),
      city: String(formData.city || "").trim(),
      vatNumber: String(formData.vatNumber || "").trim(),
      serviceType: String(formData.serviceType || "").trim(),
      status: formData.status || "active",
      notes: String(formData.notes || "").trim(),
      updatedAt: serverTimestamp(),
    };

    try {
      setSavingClient(true);
      if (editingClient?.id) {
        await updateDoc(doc(db, "invoice_clients", editingClient.id), payload);
        notify.success(t.updated);
      } else {
        await addDoc(collection(db, "invoice_clients"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        notify.success(t.saved);
      }

      clearCacheByPrefix("bauheld:clients:");
      clearCacheByPrefix("bauheld:client-profile:");
      resetForm();
      await loadClients(true);
    } catch (error) {
      console.error("Error saving client:", error);
      notify.error(language === "de" ? "Fehler beim Speichern." : "Gabim gjatë ruajtjes.");
    } finally {
      setSavingClient(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await deleteDoc(doc(db, "invoice_clients", client.id));
      clearCacheByPrefix("bauheld:clients:");
      clearCacheByPrefix(`bauheld:client-profile:${client.id}`);
      clearCacheByPrefix(`bauheld:client-profile:meta:${client.id}`);
      await loadClients(true);
      if (selectedClient?.id === client.id) {
        setSelectedClient(null);
        setProfileMeta(null);
      }
      notify.success(t.deleted);
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  const handleQuickAction = (action) => {
    if (!selectedClient) return;
    localStorage.setItem(
      "bauheld:selectedClientPreset",
      JSON.stringify({
        id: selectedClient.id,
        name: selectedClient.name,
        companyName: selectedClient.companyName,
        type: selectedClient.type,
      })
    );
    if (setActivePage) {
      if (action === "invoice") setActivePage("invoice");
      if (action === "income") setActivePage("income");
      if (action === "payment") setActivePage("payments");
    }
  };

  const cardClass =
    theme === "dark"
      ? "rounded-3xl border border-slate-700 bg-slate-950/70 p-5 shadow-sm"
      : "rounded-3xl border border-slate-200 bg-white p-5 shadow-sm";
  const inputClass =
    theme === "dark"
      ? "w-full rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 outline-none focus:border-slate-500"
      : "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-slate-400";

  return (
    <div className="space-y-6">
      <div
        className={`rounded-[32px] px-6 py-6 ${
          theme === "dark"
            ? "bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white"
            : "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white"
        }`}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight">{t.title}</h1>
            <p className="mt-2 text-sm text-slate-300">{t.subtitle}</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <UIButton
              type="button"
              onClick={() => {
                setShowForm(true);
                setEditingClient(null);
                setFormData(emptyClientForm);
              }}
              theme={theme}
              variant="secondary"
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100"
            >
              {t.addClient}
            </UIButton>
            <UIButton
              type="button"
              onClick={() => loadClients(true)}
              theme={theme}
              variant="ghost"
              size="lg"
              className="border border-white/20 bg-transparent text-white hover:bg-white/10"
            >
              {t.refresh}
            </UIButton>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr,1fr]">
        <div className="space-y-6">
          <div className={cardClass}>
            <div className="grid gap-3 md:grid-cols-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.search}
                className={`${inputClass} md:col-span-2`}
              />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className={inputClass}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === "all" ? t.all : t[opt]}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={inputClass}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === "all" ? t.all : t[opt]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={cardClass}>
            {loadingClients ? (
              <div className={`py-10 text-center text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                {t.loading}
              </div>
            ) : filteredClients.length === 0 ? (
              <div className={`py-10 text-center text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                {t.noData}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredClients.map((client) => {
                  const isSelected = selectedClient?.id === client.id;
                  const title =
                    client.type === "company"
                      ? client.companyName || client.name
                      : client.name || client.companyName;

                  return (
                    <div
                      key={client.id}
                      className={`rounded-3xl border p-4 transition ${
                        isSelected
                          ? theme === "dark"
                            ? "border-slate-500 bg-slate-900"
                            : "border-slate-900 bg-slate-50"
                          : theme === "dark"
                          ? "border-slate-700 bg-slate-950"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <button
                          type="button"
                          className="text-left"
                          onClick={() => setSelectedClient(client)}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className={`text-lg font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                              {title || "—"}
                            </h3>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              theme === "dark"
                                ? "bg-slate-800 text-slate-300"
                                : "bg-slate-100 text-slate-700"
                            }`}>
                              {t[client.type] || client.type}
                            </span>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              theme === "dark"
                                ? "bg-emerald-950 text-emerald-300"
                                : "bg-emerald-50 text-emerald-700"
                            }`}>
                              {t[client.status] || client.status}
                            </span>
                          </div>
                          <div className={`mt-2 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                            {[client.contactPerson, client.phone, client.city].filter(Boolean).join(" • ")}
                          </div>
                        </button>

                        <div className="flex flex-wrap gap-2">
                          <UIButton
                            type="button"
                            onClick={() => setSelectedClient(client)}
                            theme={theme}
                            variant="secondary"
                            size="sm"
                          >
                            {t.profile}
                          </UIButton>
                          <UIButton
                            type="button"
                            onClick={() => startEdit(client)}
                            theme={theme}
                            variant="secondary"
                            size="sm"
                          >
                            {t.editClient}
                          </UIButton>
                          <UIButton
                            type="button"
                            onClick={() => handleDelete(client)}
                            theme={theme}
                            variant="softDanger"
                            size="sm"
                          >
                            {t.deleteClient}
                          </UIButton>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {showForm && (
            <div className={cardClass}>
              <div className="mb-4 flex items-center justify-between">
                <h3 className={`text-xl font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                  {editingClient ? t.editClient : t.addClient}
                </h3>
                <UIButton type="button" onClick={resetForm} theme={theme} variant="ghost" size="sm">
                  {t.cancel}
                </UIButton>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <UILabel>{t.type}</UILabel>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData((p) => ({ ...p, type: e.target.value }))}
                    className={inputClass}
                  >
                    {TYPE_OPTIONS.filter((x) => x !== "all").map((opt) => (
                      <option key={opt} value={opt}>
                        {t[opt]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <UILabel>{t.status}</UILabel>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                    className={inputClass}
                  >
                    {STATUS_OPTIONS.filter((x) => x !== "all").map((opt) => (
                      <option key={opt} value={opt}>
                        {t[opt]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <UILabel>{t.name}</UILabel>
                  <input className={inputClass} value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} placeholder={t.name} />
                </div>
                <div>
                  <UILabel>{t.companyName}</UILabel>
                  <input className={inputClass} value={formData.companyName} onChange={(e) => setFormData((p) => ({ ...p, companyName: e.target.value }))} placeholder={t.companyName} />
                </div>
                <div>
                  <UILabel>{t.contactPerson}</UILabel>
                  <input className={inputClass} value={formData.contactPerson} onChange={(e) => setFormData((p) => ({ ...p, contactPerson: e.target.value }))} placeholder={t.contactPerson} />
                </div>
                <div>
                  <UILabel>{t.phone}</UILabel>
                  <input className={inputClass} value={formData.phone} onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))} placeholder={t.phone} />
                </div>
                <div>
                  <UILabel>{t.email}</UILabel>
                  <input className={inputClass} value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} placeholder={t.email} />
                </div>
                <div>
                  <UILabel>{t.city}</UILabel>
                  <input className={inputClass} value={formData.city} onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))} placeholder={t.city} />
                </div>
                <div className="md:col-span-2">
                  <UILabel>{t.billingAddress}</UILabel>
                  <input className={inputClass} value={formData.billingAddress} onChange={(e) => setFormData((p) => ({ ...p, billingAddress: e.target.value }))} placeholder={t.billingAddress} />
                </div>
                <div className="md:col-span-2">
                  <UILabel>{t.worksiteAddress}</UILabel>
                  <input className={inputClass} value={formData.worksiteAddress} onChange={(e) => setFormData((p) => ({ ...p, worksiteAddress: e.target.value }))} placeholder={t.worksiteAddress} />
                </div>
                <div>
                  <UILabel>{t.vatNumber}</UILabel>
                  <input className={inputClass} value={formData.vatNumber} onChange={(e) => setFormData((p) => ({ ...p, vatNumber: e.target.value }))} placeholder={t.vatNumber} />
                </div>
                <div>
                  <UILabel>{t.serviceType}</UILabel>
                  <input className={inputClass} value={formData.serviceType} onChange={(e) => setFormData((p) => ({ ...p, serviceType: e.target.value }))} placeholder={t.serviceType} />
                </div>
                <div className="md:col-span-2">
                  <UILabel>{t.notes}</UILabel>
                  <textarea
                    className={`${inputClass} min-h-[110px]`}
                    value={formData.notes}
                    onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                    placeholder={t.notes}
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-3">
                <UIButton type="button" onClick={resetForm} theme={theme} variant="secondary" size="lg">
                  {t.cancel}
                </UIButton>
                <UIButton
                  type="button"
                  onClick={handleSaveClient}
                  disabled={savingClient}
                  theme={theme}
                  variant="primary"
                  size="lg"
                >
                  {savingClient ? t.loading : editingClient ? t.update : t.save}
                </UIButton>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className={cardClass}>
            {!selectedClient ? (
              <div className={`py-10 text-center text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                {t.noClientSelected}
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className={`text-2xl font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                        {selectedClient.companyName || selectedClient.name || "—"}
                      </h2>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        theme === "dark"
                          ? "bg-slate-800 text-slate-300"
                          : "bg-slate-100 text-slate-700"
                      }`}>
                        {t[selectedClient.type] || selectedClient.type}
                      </span>
                    </div>
                    <div className={`mt-2 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                      {[selectedClient.contactPerson, selectedClient.phone, selectedClient.email].filter(Boolean).join(" • ")}
                    </div>
                    <div className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                      {[selectedClient.city, selectedClient.worksiteAddress || selectedClient.billingAddress].filter(Boolean).join(" • ")}
                    </div>
                  </div>

                  <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
                    <UIButton type="button" onClick={() => handleQuickAction("invoice")} theme={theme} variant="primary" size="sm" className="w-full sm:w-auto">
                      {t.quickInvoice}
                    </UIButton>
                    <UIButton type="button" onClick={() => handleQuickAction("income")} theme={theme} variant="secondary" size="sm" className="w-full sm:w-auto">
                      {t.quickIncome}
                    </UIButton>
                    <UIButton type="button" onClick={() => handleQuickAction("payment")} theme={theme} variant="secondary" size="sm" className="w-full sm:w-auto">
                      {t.quickPayment}
                    </UIButton>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
                  <div className={theme === "dark" ? "rounded-3xl bg-slate-900 p-4" : "rounded-3xl bg-slate-50 p-4"}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{t.totalInvoiced}</div>
                    <div className={`mt-2 text-xl sm:text-2xl font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>{formatMoney(profileMeta?.totalInvoiced || 0)}</div>
                  </div>
                  <div className={theme === "dark" ? "rounded-3xl bg-slate-900 p-4" : "rounded-3xl bg-slate-50 p-4"}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{t.totalIncome}</div>
                    <div className={`mt-2 text-xl sm:text-2xl font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>{formatMoney(profileMeta?.totalIncome || 0)}</div>
                  </div>
                  <div className={theme === "dark" ? "rounded-3xl bg-slate-900 p-4" : "rounded-3xl bg-slate-50 p-4"}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{t.totalPayments}</div>
                    <div className={`mt-2 text-xl sm:text-2xl font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>{formatMoney(profileMeta?.totalPayments || 0)}</div>
                  </div>
                  <div className={theme === "dark" ? "rounded-3xl bg-amber-950/40 p-4" : "rounded-3xl bg-amber-50 p-4"}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${theme === "dark" ? "text-amber-300" : "text-amber-700"}`}>{t.openBalance}</div>
                    <div className={`mt-2 text-xl sm:text-2xl font-black ${theme === "dark" ? "text-amber-200" : "text-amber-900"}`}>{formatMoney(profileMeta?.openBalance || 0)}</div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  {["overview", "invoices", "income", "payments"].map((tab) => (
                    <UIButton
                      key={tab}
                      type="button"
                      onClick={() => setProfileTab(tab)}
                      theme={theme}
                      variant={profileTab === tab ? "primary" : "secondary"}
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      {t[tab]}
                    </UIButton>
                  ))}
                  <UIButton
                    type="button"
                    onClick={() => {
                      if (!selectedClient?.id) return;
                      clearCacheByPrefix(`bauheld:client-profile:${selectedClient.id}`);
                      clearCacheByPrefix(`bauheld:client-profile:meta:${selectedClient.id}`);
                      loadProfileMetaData(selectedClient, true);
                      if (profileTab !== "overview") {
                        loadTabData(selectedClient, profileTab, 1, true);
                      }
                    }}
                    theme={theme}
                    variant="secondary"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    {t.refresh}
                  </UIButton>
                </div>

                <div className="mt-5">
                  {profileLoading ? (
                    <div className={`py-10 text-center text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                      {t.loading}
                    </div>
                  ) : profileTab === "overview" ? (
                    <div className={`rounded-3xl border p-5 text-sm leading-7 ${
                      theme === "dark"
                        ? "border-slate-700 bg-slate-900 text-slate-300"
                        : "border-slate-200 bg-slate-50 text-slate-700"
                    }`}>
                      {selectedClient.notes || t.noData}
                    </div>
                  ) : (
                    <div className={`overflow-hidden rounded-3xl border ${theme === "dark" ? "border-slate-700" : "border-slate-200"}`}>
                      <div className="block sm:hidden">
                        {tabRows[profileTab]?.length ? (
                          <div className="space-y-3 p-3">
                            {tabRows[profileTab].map((row) => (
                              <div
                                key={row.id}
                                className={`rounded-2xl border p-3 ${
                                  theme === "dark"
                                    ? "border-slate-700 bg-slate-900"
                                    : "border-slate-200 bg-white"
                                }`}
                              >
                                {profileTab === "invoices" && (
                                  <>
                                    <div className={`text-sm font-bold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                                      {row.invoiceNumber || row.number || "—"}
                                    </div>
                                    <div className={`mt-2 grid grid-cols-2 gap-2 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                                      <div>
                                        <div className="font-semibold">{t.date}</div>
                                        <div>{formatDate(row.invoiceDate || row.date || row.createdAt)}</div>
                                      </div>
                                      <div>
                                        <div className="font-semibold">{t.dueDate}</div>
                                        <div>{formatDate(row.dueDate)}</div>
                                      </div>
                                      <div>
                                        <div className="font-semibold">{t.amount}</div>
                                        <div className="font-bold">{formatMoney(row.grandTotal || row.total || 0)}</div>
                                      </div>
                                      <div>
                                        <div className="font-semibold">{t.paid}</div>
                                        <div>{formatMoney(row.paidAmount || 0)}</div>
                                      </div>
                                    </div>
                                  </>
                                )}

                                {profileTab === "income" && (
                                  <>
                                    <div className={`text-sm font-bold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                                      {formatMoney(row.amount || 0)}
                                    </div>
                                    <div className={`mt-2 grid grid-cols-1 gap-2 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                                      <div>
                                        <div className="font-semibold">{t.date}</div>
                                        <div>{formatDate(row.date || row.createdAt)}</div>
                                      </div>
                                      <div>
                                        <div className="font-semibold">{t.notes}</div>
                                        <div>{row.notes || row.description || "—"}</div>
                                      </div>
                                    </div>
                                  </>
                                )}

                                {profileTab === "payments" && (
                                  <>
                                    <div className={`text-sm font-bold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                                      {formatMoney(row.amount || 0)}
                                    </div>
                                    <div className={`mt-2 grid grid-cols-1 gap-2 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                                      <div>
                                        <div className="font-semibold">{t.date}</div>
                                        <div>{formatDate(row.date || row.createdAt)}</div>
                                      </div>
                                      <div>
                                        <div className="font-semibold">{t.notes}</div>
                                        <div>{row.notes || row.description || "—"}</div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={`px-4 py-10 text-center text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                            {t.noData}
                          </div>
                        )}
                      </div>

                      <div className="hidden sm:block overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className={theme === "dark" ? "bg-slate-900 text-left" : "bg-slate-50 text-left"}>
                            <tr>
                              {profileTab === "invoices" && (
                                <>
                                  <th className="px-4 py-3 font-semibold">{t.invoiceNo}</th>
                                  <th className="px-4 py-3 font-semibold">{t.date}</th>
                                  <th className="px-4 py-3 font-semibold">{t.dueDate}</th>
                                  <th className="px-4 py-3 font-semibold">{t.amount}</th>
                                  <th className="px-4 py-3 font-semibold">{t.paid}</th>
                                </>
                              )}
                              {profileTab === "income" && (
                                <>
                                  <th className="px-4 py-3 font-semibold">{t.date}</th>
                                  <th className="px-4 py-3 font-semibold">{t.amount}</th>
                                  <th className="px-4 py-3 font-semibold">{t.notes}</th>
                                </>
                              )}
                              {profileTab === "payments" && (
                                <>
                                  <th className="px-4 py-3 font-semibold">{t.date}</th>
                                  <th className="px-4 py-3 font-semibold">{t.amount}</th>
                                  <th className="px-4 py-3 font-semibold">{t.notes}</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {tabRows[profileTab]?.length ? (
                              tabRows[profileTab].map((row) => (
                                <tr key={row.id} className={theme === "dark" ? "border-t border-slate-800" : "border-t border-slate-100"}>
                                  {profileTab === "invoices" && (
                                    <>
                                      <td className="px-4 py-3">{row.invoiceNumber || row.number || "—"}</td>
                                      <td className="px-4 py-3">{formatDate(row.invoiceDate || row.date || row.createdAt)}</td>
                                      <td className="px-4 py-3">{formatDate(row.dueDate)}</td>
                                      <td className="px-4 py-3 font-semibold">{formatMoney(row.grandTotal || row.total || 0)}</td>
                                      <td className="px-4 py-3">{formatMoney(row.paidAmount || 0)}</td>
                                    </>
                                  )}
                                  {profileTab === "income" && (
                                    <>
                                      <td className="px-4 py-3">{formatDate(row.date || row.createdAt)}</td>
                                      <td className="px-4 py-3 font-semibold">{formatMoney(row.amount || 0)}</td>
                                      <td className={`px-4 py-3 ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{row.notes || row.description || "—"}</td>
                                    </>
                                  )}
                                  {profileTab === "payments" && (
                                    <>
                                      <td className="px-4 py-3">{formatDate(row.date || row.createdAt)}</td>
                                      <td className="px-4 py-3 font-semibold">{formatMoney(row.amount || 0)}</td>
                                      <td className={`px-4 py-3 ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{row.notes || row.description || "—"}</td>
                                    </>
                                  )}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className={`px-4 py-10 text-center ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                                  {t.noData}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className={`flex items-center justify-between border-t px-4 py-3 ${
                        theme === "dark" ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-white"
                      }`}>
                        <UIButton
                          type="button"
                          disabled={tabPages[profileTab] <= 1}
                          onClick={() =>
                            loadTabData(selectedClient, profileTab, Math.max(1, tabPages[profileTab] - 1), false)
                          }
                          theme={theme}
                          variant="secondary"
                          size="sm"
                        >
                          {t.prev}
                        </UIButton>
                        <div className={`text-xs sm:text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                          {t.page} {tabPages[profileTab]}
                        </div>
                        <UIButton
                          type="button"
                          disabled={!tabHasMore[profileTab]}
                          onClick={() =>
                            loadTabData(selectedClient, profileTab, tabPages[profileTab] + 1, false)
                          }
                          theme={theme}
                          variant="secondary"
                          size="sm"
                        >
                          {t.next}
                        </UIButton>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
